import { memo } from 'react';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useCurrencyFormatter } from '@/hooks/useCurrencyFormatter';
import { useCurrencyDisplayStore } from '../store/currency-display-store';
import { cn } from '@/lib/utils';

/**
 * BLU-373 PR2b: render a money value, currency-aware.
 *
 * The `amount` is the value as stored on the PO row (always in PO currency).
 * - poCurrency: the PO's currency code (`order.currency`)
 * - exchangeRate: the PO's frozen exchange_rate (`order.exchange_rate`).
 *   exchange_rate semantics: org base per supplier currency unit
 *   → unit_cost_org_base = unit_cost_po * exchange_rate
 *
 * When the toggle store is in 'org' mode, the amount is converted to org
 * base for display. When in 'po' mode, the value is shown as-stored.
 *
 * If the PO currency matches the org currency, both modes render the same
 * number — the toggle pill is hidden in that case so the user never sees
 * the no-op switch.
 *
 * Memoized so the 500-row line item table only re-renders the cells that
 * actually changed when the toggle flips.
 *
 * MATH SOURCE OF TRUTH: this conversion intentionally mirrors the backend
 * `convertUnitCostToOrgBase` in `apps/backend/src/lib/money.ts`. The two
 * implementations must stay in sync (same scaling, same MAX_EXCHANGE_RATE,
 * same fallback semantics on invalid rate). If the backend math changes,
 * update this file too.
 */
interface MoneyProps {
  amount: number | null | undefined;
  poCurrency: string | null | undefined;
  exchangeRate: number | null | undefined;
  /** Optional className for the wrapping span (e.g. text alignment). */
  className?: string;
  /** When true, render `—` for null/undefined; otherwise render `$0.00`. */
  emptyAsDash?: boolean;
  /**
   * When true, append the ISO currency code (e.g. "USD"/"AUD") after the
   * amount. We render with `narrowSymbol` (a bare "$" for both USD and AUD),
   * so summary/total contexts set this to disambiguate which currency a value
   * is in — the code reflects what's actually displayed (PO currency in
   * po-mode, org currency when toggled to org-base).
   */
  showCode?: boolean;
}

/** Mirrors apps/backend/src/lib/money.ts MAX_EXCHANGE_RATE. */
const MAX_EXCHANGE_RATE = 10_000;

const isValidRate = (rate: number | null | undefined): rate is number => {
  if (rate == null) return false;
  if (!Number.isFinite(rate)) return false;
  if (rate <= 0) return false;
  if (rate > MAX_EXCHANGE_RATE) return false;
  return true;
};

const MoneyInner = ({ amount, poCurrency, exchangeRate, className, emptyAsDash, showCode }: MoneyProps) => {
  const { organization } = useOrganization();
  const orgCurrency = organization?.default_currency ?? 'AUD';
  const { format } = useCurrencyFormatter();
  const displayCurrency = useCurrencyDisplayStore((s) => s.displayCurrency);

  // Muted ISO-code suffix so a bare "$" (narrowSymbol) is never ambiguous.
  const code = (currency: string) =>
    showCode ? (
      <span className="ml-1 text-xs font-normal text-muted-foreground">{currency}</span>
    ) : null;

  if (amount == null) {
    if (emptyAsDash) return <span className={cn('tabular-nums', className)}>-</span>;
    return (
      <span className={cn('tabular-nums', className)}>
        {format(0, orgCurrency)}
        {code(orgCurrency)}
      </span>
    );
  }

  const effectivePoCurrency = poCurrency ?? orgCurrency;
  const sameCurrency = effectivePoCurrency === orgCurrency;
  const showOrgBase = !sameCurrency && displayCurrency === 'org';

  // BLU-373 PR2b polish: brand-purple tint when showing converted (org-base)
  // values. Visual confirmation that the toggle worked, and ties to the
  // active pill segment colour. PO-currency mode and same-currency POs
  // render in the default neutral tone.
  const convertedTint = showOrgBase ? 'text-threadheads-purple' : '';

  if (showOrgBase) {
    if (!isValidRate(exchangeRate)) {
      // Bad rate data — fall back to PO currency display rather than nonsense
      return (
        <span className={cn('tabular-nums', className)}>
          {format(amount, effectivePoCurrency)}
          {code(effectivePoCurrency)}
        </span>
      );
    }
    const converted = Math.round(amount * exchangeRate * 10000) / 10000;
    return (
      <span className={cn('tabular-nums', convertedTint, className)}>
        {format(converted, orgCurrency)}
        {code(orgCurrency)}
      </span>
    );
  }

  return (
    <span className={cn('tabular-nums', className)}>
      {format(amount, effectivePoCurrency)}
      {code(effectivePoCurrency)}
    </span>
  );
};

export const Money = memo(MoneyInner);
